import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import MarketplaceCard from '../components/MarketplaceCard';
import { Check, X } from '../components/icons';
import { fetchMarketplaceListing, sendInquiry, type MarketplaceListing as Listing } from '../lib/marketplace-api';
import { formatPrice, categoryLabel, CONDITION_LABELS } from '../lib/marketplace-categories';
import { getUser } from '../lib/marketplace-auth';

export default function MarketplaceListing() {
  const { slug } = useParams<{ slug: string }>();
  const [listing, setListing] = useState<Listing | null>(null);
  const [related, setRelated] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeImage, setActiveImage] = useState(0);

  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setNotFound(false);
    setActiveImage(0);
    setSent(false);
    fetchMarketplaceListing(slug)
      .then((data) => {
        setListing(data.listing);
        setRelated(data.related || []);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  // Pre-fill inquiry form if logged in
  useEffect(() => {
    const user = getUser();
    if (user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || '',
        email: f.email || user.email || '',
        phone: f.phone || user.phone || '',
      }));
    }
  }, [listing]);

  const handleInquiry = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!listing) return;
    setFormError('');
    setSending(true);
    try {
      await sendInquiry({
        listing_id: listing.id,
        buyer_name: form.name,
        buyer_email: form.email,
        buyer_phone: form.phone || undefined,
        message: form.message,
      });
      setSent(true);
    } catch (err: any) {
      setFormError(err.message || 'Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-12 animate-pulse">
        <div className="h-4 w-48 bg-surface-light dark:bg-surface rounded mb-6" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="aspect-[4/3] bg-surface-light dark:bg-surface rounded-lg" />
          <div className="space-y-4">
            <div className="h-8 w-3/4 bg-surface-light dark:bg-surface rounded" />
            <div className="h-6 w-1/3 bg-surface-light dark:bg-surface rounded" />
            <div className="h-32 w-full bg-surface-light dark:bg-surface rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !listing) {
    return (
      <div className="max-w-[600px] mx-auto px-4 py-24 text-center">
        <div className="text-5xl mb-4">⚓</div>
        <h1 className="font-display text-2xl font-bold mb-2">Listing not found</h1>
        <p className="text-text-secondary text-sm mb-6">This item may have been sold or removed.</p>
        <Link to="/marketplace" className="btn btn-primary">Browse marketplace</Link>
      </div>
    );
  }

  const images = listing.images || [];
  const specs: [string, string][] = [
    ['Brand', listing.brand],
    ['Model', listing.model],
    ['Year', listing.year ? String(listing.year) : null],
    ['Length', listing.length_m ? `${listing.length_m} m` : null],
    ['Engine hours', listing.engine_hours != null ? `${listing.engine_hours} hrs` : null],
    ['Condition', CONDITION_LABELS[listing.condition] || listing.condition],
    ['Category', categoryLabel(listing.category)],
    ['Subcategory', listing.subcategory],
    ['Location', [listing.city, listing.region].filter(Boolean).join(', ')],
  ].filter(([, v]) => v) as [string, string][];

  return (
    <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-6 md:py-10 pb-24">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-text-muted mb-5 flex-wrap">
        <Link to="/" className="text-text-secondary hover:text-accent transition-colors">Home</Link>
        <span>/</span>
        <Link to="/marketplace" className="text-text-secondary hover:text-accent transition-colors">Marketplace</Link>
        <span>/</span>
        <Link to={`/marketplace?category=${listing.category}`} className="text-text-secondary hover:text-accent transition-colors">
          {categoryLabel(listing.category)}
        </Link>
        <span>/</span>
        <span className="truncate max-w-[200px]">{listing.title}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 md:gap-10">
        {/* Left: gallery + description */}
        <div>
          {/* Gallery */}
          <div className="rounded-lg overflow-hidden bg-surface-light dark:bg-surface border border-[rgba(16,42,66,0.09)] dark:border-[rgba(214,233,247,0.09)]">
            {images.length > 0 ? (
              <img
                src={images[activeImage]}
                alt={listing.title}
                className="w-full aspect-[4/3] object-cover"
              />
            ) : (
              <div className="w-full aspect-[4/3] flex items-center justify-center text-text-muted">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <path d="m21 15-5-5L5 21" />
                </svg>
              </div>
            )}
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImage(i)}
                  className={`flex-shrink-0 w-20 h-16 rounded-md overflow-hidden border-2 transition-colors ${
                    i === activeImage ? 'border-accent' : 'border-transparent opacity-70 hover:opacity-100'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          {/* Description */}
          <div className="mt-8">
            <h2 className="font-display text-lg font-bold mb-3">Description</h2>
            <div className="text-text-secondary text-sm leading-relaxed whitespace-pre-wrap">
              {listing.description || 'No description provided.'}
            </div>
          </div>
        </div>

        {/* Right: price, specs, seller, inquiry */}
        <div className="space-y-5">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-0.5 rounded text-[0.65rem] font-bold uppercase tracking-wide bg-accent/10 text-accent border border-accent/20">
                {categoryLabel(listing.category)}
              </span>
              <span className="px-2 py-0.5 rounded text-[0.65rem] font-bold uppercase tracking-wide bg-surface-light dark:bg-surface text-text-secondary border border-[rgba(79,179,217,0.15)]">
                {CONDITION_LABELS[listing.condition] || listing.condition}
              </span>
            </div>
            <h1 className="font-display text-2xl md:text-[1.75rem] font-bold tracking-tight leading-snug">
              {listing.title}
            </h1>
            <div className="font-display text-2xl md:text-3xl font-extrabold text-accent mt-3">
              {formatPrice(listing.price, listing.price_type)}
            </div>
            <p className="text-text-muted text-xs mt-2">
              Listed {new Date(listing.created_at).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' })}
              {listing.views ? ` · ${listing.views} views` : ''}
            </p>
          </div>

          {/* Specs */}
          {specs.length > 0 && (
            <div className="bg-card-light dark:bg-card border border-[rgba(16,42,66,0.09)] dark:border-[rgba(214,233,247,0.09)] rounded-lg p-4">
              <h2 className="font-semibold text-sm mb-3">Details</h2>
              <dl className="space-y-2">
                {specs.map(([label, value]) => (
                  <div key={label} className="flex justify-between gap-4 text-sm">
                    <dt className="text-text-muted">{label}</dt>
                    <dd className="font-medium text-right">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {/* Seller */}
          <div className="bg-card-light dark:bg-card border border-[rgba(16,42,66,0.09)] dark:border-[rgba(214,233,247,0.09)] rounded-lg p-4 flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-accent/15 text-accent flex items-center justify-center font-display font-bold text-lg flex-shrink-0">
              {(listing.seller_name || '?').charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="font-semibold text-sm">{listing.seller_name}</div>
              <div className="text-text-muted text-xs">{listing.seller_region || listing.region}</div>
            </div>
          </div>

          {/* Inquiry form */}
          <div className="bg-card-light dark:bg-card border border-[rgba(16,42,66,0.09)] dark:border-[rgba(214,233,247,0.09)] rounded-lg p-5">
            {sent ? (
              <div className="text-center py-4">
                <div className="w-12 h-12 rounded-full bg-emerald-500/15 text-emerald-500 flex items-center justify-center mx-auto mb-3">
                  <Check className="w-6 h-6" />
                </div>
                <h2 className="font-semibold mb-1">Message sent!</h2>
                <p className="text-text-secondary text-sm">The seller will get back to you via email.</p>
              </div>
            ) : (
              <>
                <h2 className="font-semibold text-sm mb-4">Message the seller</h2>
                {formError && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4 text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
                    <X className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    {formError}
                  </div>
                )}
                <form onSubmit={handleInquiry} className="space-y-3">
                  <input
                    className="form-input"
                    type="text"
                    required
                    placeholder="Your name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                  <input
                    className="form-input"
                    type="email"
                    required
                    placeholder="Your email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                  <input
                    className="form-input"
                    type="tel"
                    placeholder="Phone (optional)"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                  <textarea
                    className="form-input min-h-[100px]"
                    required
                    placeholder={`Hi, I'm interested in your ${listing.title}…`}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                  />
                  <button type="submit" disabled={sending} className="btn btn-primary btn-block">
                    {sending ? 'Sending…' : 'Send message'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Related listings */}
      {related.length > 0 && (
        <section className="mt-14">
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight">More {categoryLabel(listing.category).toLowerCase()}</h2>
            <Link to={`/marketplace?category=${listing.category}`} className="text-sm font-semibold text-accent hover:underline">
              View all →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
            {related.map((l) => <MarketplaceCard key={l.id} listing={l} />)}
          </div>
        </section>
      )}
    </div>
  );
}
